import {
  authorizedOperators,
  businessDate,
  CARD_TECHS,
  DOCUMENT_KINDS,
  normalizeCardUid,
  operatorStatus,
  OPERATOR_STATUSES,
  VEHICLE_TYPES,
  type CardTech,
  type DocumentKind,
  type Operator,
  type OperatorCard,
  type OperatorDocument,
  type OperatorPermission,
  type OperatorStatus,
  type Vehicle,
  type VehicleType,
} from "@fts/core";
import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { ConfirmDialog, Dialog, FormError, FormField, fieldAria } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { EmptyState, Mono, Note, PageHeader, Panel } from "../components/ui";
import type { AuthlistStatus } from "../data/provider";
import { useI18n } from "../i18n";
import { sitePath } from "../lib/fleet";
import { relativeTime } from "../lib/format";
import {
  addDays,
  AUTHLIST_TONE,
  authlistState,
  canManageOperators,
  documentWarning,
  formatDay,
  OPERATOR_STATUS_TONE,
  permissionExpired,
} from "../lib/operators";
import { navigate } from "../router/hash";
import { useSession, type TenantData } from "../state/session";

/* ---------------- shared ---------------- */

const today = () => businessDate(new Date());

/** After a write: ask the API to push the changed device lists now, then reload the tenant data. */
function useOperatorWrite() {
  const { provider, tenant, refresh } = useSession();
  return async (fn: () => Promise<unknown>) => {
    await fn();
    if (tenant) await provider.syncAuthlists(tenant.id).catch(() => undefined); // the minute cron catches up anyway
    await refresh();
  };
}

function siteOptions(data: TenantData): { id: string; label: string }[] {
  return data.sites
    .map((s) => ({ id: s.id, label: sitePath(data.sites, s.id).map((x) => x.name).join(" / ") }))
    .sort((a, b) => a.label.localeCompare(b.label, "tr"));
}

const siteLabel = (data: TenantData, id: string | null) => (id ? sitePath(data.sites, id).map((s) => s.name).join(" / ") : null);

export function OperatorStatusBadge({ status }: { status: OperatorStatus }) {
  const { t } = useI18n();
  return <span className={`badge tone-${OPERATOR_STATUS_TONE[status]}`}>{t(`opStatus.${status}`)}</span>;
}

/** Card-list delivery of a device; `noReader`: the device has no card reader (ADR 0023), no list exists. */
export function AuthlistBadge({ status, noReader = false }: { status: AuthlistStatus | undefined; noReader?: boolean }) {
  const { t, locale } = useI18n();
  const s = authlistState(status);
  if (noReader) {
    return (
      <span className="muted" title={t("authlist.noReaderHint")}>
        {t("authlist.noReader")}
      </span>
    );
  }
  const title =
    s === "synced"
      ? t("authlist.syncedAt", { when: relativeTime(status!.ackedAt, locale) ?? "" })
      : s === "pending"
        ? t("authlist.pendingHint")
        : s === "error"
          ? t("authlist.errorHint", { error: status!.error ?? "" })
          : t("authlist.noneHint");
  return (
    <span className={`badge tone-${AUTHLIST_TONE[s]}`} title={title}>
      {t(`authlist.${s}`)}
    </span>
  );
}

function DayCell({ day, empty }: { day: string | null; empty?: ReactNode }) {
  const { t, locale } = useI18n();
  return day ? <span className="nowrap">{formatDay(day, locale)}</span> : <span className="muted">{empty ?? t("common.empty")}</span>;
}

function DocWarningBadge({ doc, now }: { doc: OperatorDocument; now: string }) {
  const { t } = useI18n();
  const w = documentWarning(doc, now);
  if (w === null) return null;
  if (w === "expired") return <span className="badge tone-crit">{t("doc.expired")}</span>;
  return <span className={`badge tone-${w === 30 ? "info" : "warn"}`}>{t("doc.withinDays", { days: w })}</span>;
}

function permissionScope(data: TenantData, p: OperatorPermission, t: ReturnType<typeof useI18n>["t"]): string {
  if (p.vehicleId) {
    const v = data.vehicles.find((x) => x.id === p.vehicleId);
    return t("perm.scopeVehicle", { vehicle: v?.fleetNo ?? t("common.empty") });
  }
  const site = siteLabel(data, p.siteId) ?? t("common.empty");
  return p.vehicleType ? t("perm.scopeSiteType", { site, type: t(`vehicleType.${p.vehicleType}`) }) : t("perm.scopeSite", { site });
}

/* ---------------- dialogs ---------------- */

function DialogButtons({ busy, submitLabel, onClose }: { busy: boolean; submitLabel: string; onClose: () => void }) {
  const { t } = useI18n();
  return (
    <>
      <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
        {t("common.cancel")}
      </button>
      <button type="submit" className="button button-primary" disabled={busy}>
        {busy ? t("common.working") : submitLabel}
      </button>
    </>
  );
}

/** Shared submit state for the dialogs below. */
function useSubmit(onDone: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onDone();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };
  return { busy, error, run };
}

export function OperatorFormDialog({ data, operator, onClose, onSaved }: { data: TenantData; operator?: Operator; onClose: () => void; onSaved?: (id: string) => void }) {
  const { t } = useI18n();
  const { provider, tenant } = useSession();
  const write = useOperatorWrite();
  const [fullName, setFullName] = useState(operator?.fullName ?? "");
  const [employeeNo, setEmployeeNo] = useState(operator?.employeeNo ?? "");
  const [siteId, setSiteId] = useState(operator?.siteId ?? "");
  const [active, setActive] = useState(operator?.active ?? true);
  const [touched, setTouched] = useState(false);
  const { busy, error, run } = useSubmit(onClose);
  const nameError = touched && !fullName.trim() ? t("form.required") : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!tenant || !fullName.trim()) return;
    void run(async () => {
      let saved: Operator | null = null;
      await write(async () => {
        saved = await provider.saveOperator({
          id: operator?.id,
          tenantId: tenant.id,
          siteId: siteId || null,
          fullName,
          employeeNo: employeeNo.trim() || null,
          active,
          leftOn: operator?.leftOn ?? null,
        });
      });
      if (saved) onSaved?.((saved as Operator).id);
    });
  };

  return (
    <Dialog
      title={operator ? t("operator.editTitle") : t("operator.addTitle")}
      description={t("operator.kvkkNote")}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={<DialogButtons busy={busy} onClose={onClose} submitLabel={t("common.save")} />}
    >
      <FormField id="op-name" label={t("operator.fullName")} error={nameError}>
        <input {...fieldAria("op-name", { error: nameError })} value={fullName} maxLength={120} autoComplete="off" onChange={(e) => setFullName(e.target.value)} data-autofocus />
      </FormField>
      <FormField id="op-no" label={t("operator.employeeNo")} optional>
        <input id="op-no" className="mono" value={employeeNo} maxLength={40} autoComplete="off" onChange={(e) => setEmployeeNo(e.target.value)} />
      </FormField>
      <FormField id="op-site" label={t("operator.site")} hint={t("operator.siteHint")}>
        <select {...fieldAria("op-site", { hint: true })} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
          <option value="">{t("operator.siteAll")}</option>
          {siteOptions(data).map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </FormField>
      {operator && (
        <label className="check-row">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <span>{t("operator.activeLabel")}</span>
        </label>
      )}
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

function LeaveDialog({ operator, onClose }: { operator: Operator; onClose: () => void }) {
  const { t } = useI18n();
  const { provider } = useSession();
  const write = useOperatorWrite();
  const [day, setDay] = useState(operator.leftOn ?? today());
  const { busy, error, run } = useSubmit(onClose);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return;
    void run(() => write(() => provider.saveOperator({ ...operator, leftOn: day })));
  };
  return (
    <Dialog
      title={t("operator.leaveTitle", { name: operator.fullName })}
      description={t("operator.leaveDescription")}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={<DialogButtons busy={busy} onClose={onClose} submitLabel={t("operator.leaveSubmit")} />}
    >
      <FormField id="op-left" label={t("operator.leftOn")} hint={t("operator.leftOnHint")}>
        <input {...fieldAria("op-left", { hint: true })} type="date" value={day} min={addDays(today(), -31)} onChange={(e) => setDay(e.target.value)} data-autofocus />
      </FormField>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

function CardDialog({ operator, onClose }: { operator: Operator; onClose: () => void }) {
  const { t } = useI18n();
  const { provider } = useSession();
  const write = useOperatorWrite();
  const [raw, setRaw] = useState("");
  const [tech, setTech] = useState<CardTech>("mifare");
  const [touched, setTouched] = useState(false);
  const { busy, error, run } = useSubmit(onClose);
  const uid = normalizeCardUid(raw);
  const uidError = touched && !uid ? t("card.uidFormat") : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!uid) return;
    void run(() => write(() => provider.addCard({ tenantId: operator.tenantId, operatorId: operator.id, uid, tech })));
  };
  return (
    <Dialog
      title={t("card.addTitle", { name: operator.fullName })}
      description={t("card.addDescription")}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={<DialogButtons busy={busy} onClose={onClose} submitLabel={t("card.add")} />}
    >
      <FormField id="card-uid" label={t("card.uid")} hint={uid && uid !== raw.trim() ? t("card.uidNormalized", { uid }) : t("card.uidHint")} error={uidError}>
        <input
          {...fieldAria("card-uid", { hint: true, error: uidError })}
          className="mono input-upper"
          autoComplete="off"
          spellCheck={false}
          placeholder="04A1B2C3D4E5F6"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          data-autofocus
        />
      </FormField>
      <FormField id="card-tech" label={t("card.tech")}>
        <select id="card-tech" value={tech} onChange={(e) => setTech(e.target.value as CardTech)}>
          {CARD_TECHS.map((x) => (
            <option key={x} value={x}>
              {t(`cardTech.${x}`)}
            </option>
          ))}
        </select>
      </FormField>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

function DocumentDialog({ operator, doc, onClose }: { operator: Operator; doc?: OperatorDocument; onClose: () => void }) {
  const { t } = useI18n();
  const { provider } = useSession();
  const write = useOperatorWrite();
  const [kind, setKind] = useState<DocumentKind>(doc?.kind ?? "operator_certificate");
  const [label, setLabel] = useState(doc?.label ?? "");
  const [validUntil, setValidUntil] = useState(doc?.validUntil ?? addDays(today(), 365));
  const [required, setRequired] = useState(doc?.required ?? true);
  const [touched, setTouched] = useState(false);
  const { busy, error, run } = useSubmit(onClose);
  const dateError = touched && !/^\d{4}-\d{2}-\d{2}$/.test(validUntil) ? t("form.required") : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (dateError || !/^\d{4}-\d{2}-\d{2}$/.test(validUntil)) return;
    void run(() =>
      write(() => provider.saveDocument({ id: doc?.id, tenantId: operator.tenantId, operatorId: operator.id, kind, label: label.trim() || null, validUntil, required })),
    );
  };
  return (
    <Dialog
      title={doc ? t("doc.editTitle") : t("doc.addTitle")}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={<DialogButtons busy={busy} onClose={onClose} submitLabel={t("common.save")} />}
    >
      <FormField id="doc-kind" label={t("doc.kind")}>
        <select id="doc-kind" value={kind} onChange={(e) => setKind(e.target.value as DocumentKind)} data-autofocus>
          {DOCUMENT_KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`docKind.${k}`)}
            </option>
          ))}
        </select>
      </FormField>
      <FormField id="doc-label" label={t("doc.label")} optional>
        <input id="doc-label" value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} />
      </FormField>
      <FormField id="doc-until" label={t("doc.validUntil")} error={dateError}>
        <input {...fieldAria("doc-until", { error: dateError })} type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
      </FormField>
      <label className="check-row">
        <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
        <span>{t("doc.requiredLabel")}</span>
      </label>
      <Note>{t("doc.requiredHint")}</Note>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

function PermissionDialog({ data, operator, onClose }: { data: TenantData; operator?: Operator; onClose: () => void }) {
  const { t } = useI18n();
  const { provider } = useSession();
  const write = useOperatorWrite();
  const candidates = useMemo(
    () => data.operators.operators.filter((o) => o.active && !o.anonymized && (!o.leftOn || o.leftOn >= today())).sort((a, b) => a.fullName.localeCompare(b.fullName, "tr")),
    [data.operators.operators],
  );
  const [operatorId, setOperatorId] = useState(operator?.id ?? "");
  const [scope, setScope] = useState<"site" | "vehicle">("site");
  const [siteId, setSiteId] = useState("");
  const [vehicleType, setVehicleType] = useState<VehicleType | "">("");
  const [vehicleId, setVehicleId] = useState("");
  const [until, setUntil] = useState("");
  const [touched, setTouched] = useState(false);
  const { busy, error, run } = useSubmit(onClose);
  const vehicles = useMemo(() => data.vehicles.filter((v) => v.active).sort((a, b) => a.fleetNo.localeCompare(b.fleetNo, "tr", { numeric: true })), [data.vehicles]);
  const opError = touched && !operatorId ? t("form.chooseOne") : null;
  const scopeError = touched && (scope === "site" ? !siteId : !vehicleId) ? t("form.chooseOne") : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    const op = data.operators.operators.find((o) => o.id === operatorId);
    if (!op || (scope === "site" ? !siteId : !vehicleId)) return;
    void run(() =>
      write(() =>
        provider.addPermission({
          tenantId: op.tenantId,
          operatorId: op.id,
          siteId: scope === "site" ? siteId : null,
          vehicleType: scope === "site" && vehicleType ? vehicleType : null,
          vehicleId: scope === "vehicle" ? vehicleId : null,
          validUntil: until || null,
        }),
      ),
    );
  };

  return (
    <Dialog
      title={operator ? t("perm.addTitleFor", { name: operator.fullName }) : t("perm.addTitle")}
      description={t("perm.addDescription")}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={<DialogButtons busy={busy} onClose={onClose} submitLabel={t("perm.add")} />}
    >
      {!operator && (
        <FormField id="perm-op" label={t("perm.operator")} error={opError}>
          <select {...fieldAria("perm-op", { error: opError })} value={operatorId} onChange={(e) => setOperatorId(e.target.value)} data-autofocus>
            <option value="">{t("form.select")}</option>
            {candidates.map((o) => (
              <option key={o.id} value={o.id}>
                {o.fullName}
                {o.employeeNo ? ` · ${o.employeeNo}` : ""}
              </option>
            ))}
          </select>
        </FormField>
      )}
      <fieldset className="fieldset">
        <legend>{t("perm.scope")}</legend>
        <label className="check-row">
          <input type="radio" name="perm-scope" checked={scope === "site"} onChange={() => setScope("site")} {...(operator ? { "data-autofocus": true } : {})} />
          <span>{t("perm.scopeSiteOption")}</span>
        </label>
        <label className="check-row">
          <input type="radio" name="perm-scope" checked={scope === "vehicle"} onChange={() => setScope("vehicle")} />
          <span>{t("perm.scopeVehicleOption")}</span>
        </label>
      </fieldset>
      {scope === "site" ? (
        <>
          <FormField id="perm-site" label={t("perm.site")} hint={t("perm.siteHint")} error={scopeError}>
            <select {...fieldAria("perm-site", { hint: true, error: scopeError })} value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">{t("form.select")}</option>
              {siteOptions(data).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </FormField>
          <FormField id="perm-type" label={t("perm.vehicleType")}>
            <select id="perm-type" value={vehicleType} onChange={(e) => setVehicleType(e.target.value as VehicleType | "")}>
              <option value="">{t("perm.allTypes")}</option>
              {VEHICLE_TYPES.map((vt) => (
                <option key={vt} value={vt}>
                  {t(`vehicleType.${vt}`)}
                </option>
              ))}
            </select>
          </FormField>
        </>
      ) : (
        <FormField id="perm-vehicle" label={t("perm.vehicle")} error={scopeError}>
          <select {...fieldAria("perm-vehicle", { error: scopeError })} value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
            <option value="">{t("form.select")}</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.fleetNo} · {siteLabel(data, v.siteId)}
              </option>
            ))}
          </select>
        </FormField>
      )}
      <FormField id="perm-until" label={t("perm.validUntil")} hint={t("perm.validUntilHint")} optional>
        <input {...fieldAria("perm-until", { hint: true })} type="date" value={until} min={today()} onChange={(e) => setUntil(e.target.value)} />
      </FormField>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

function CardStatusDialog({ card, status, operatorName, onClose }: { card: OperatorCard; status: "lost" | "revoked"; operatorName: string; onClose: () => void }) {
  const { t } = useI18n();
  const { provider } = useSession();
  const write = useOperatorWrite();
  return (
    <ConfirmDialog
      title={status === "lost" ? t("card.lostTitle") : t("card.revokeTitle")}
      confirmLabel={status === "lost" ? t("card.markLost") : t("card.revoke")}
      danger
      onClose={onClose}
      onConfirm={async () => {
        await write(() => provider.setCardStatus(card.id, status));
        onClose();
      }}
    >
      <p>{t("card.statusBody", { uid: card.uid, name: operatorName })}</p>
    </ConfirmDialog>
  );
}

/* ---------------- tables ---------------- */

function CardsTable({ data, cards, showOperator, canManage }: { data: TenantData; cards: OperatorCard[]; showOperator?: boolean; canManage: boolean }) {
  const { t, locale } = useI18n();
  const [modal, setModal] = useState<{ card: OperatorCard; status: "lost" | "revoked" } | null>(null);
  const name = (id: string) => data.operators.operators.find((o) => o.id === id)?.fullName ?? t("common.empty");
  const sorted = [...cards].sort((a, b) => (a.status === b.status ? a.uid.localeCompare(b.uid) : a.status === "active" ? -1 : b.status === "active" ? 1 : 0));
  return (
    <>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">{t("card.uid")}</th>
              {showOperator && <th scope="col">{t("card.operator")}</th>}
              <th scope="col" className="col-hide-sm">
                {t("card.tech")}
              </th>
              <th scope="col">{t("card.status")}</th>
              <th scope="col" className="col-hide-md">
                {t("card.createdAt")}
              </th>
              {canManage && (
                <th scope="col" className="col-actions">
                  <span className="sr-only">{t("common.actions")}</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => (
              <tr key={c.id}>
                <td className="nowrap">
                  <Mono>{c.uid}</Mono>
                </td>
                {showOperator && (
                  <td>
                    <a href={`#/operators/${encodeURIComponent(c.operatorId)}`} className="row-link">
                      {name(c.operatorId)}
                    </a>
                  </td>
                )}
                <td className="col-hide-sm">{t(`cardTech.${c.tech}`)}</td>
                <td>
                  <span className={`badge tone-${c.status === "active" ? "ok" : c.status === "lost" ? "warn" : "neutral"}`}>{t(`cardStatus.${c.status}`)}</span>
                </td>
                <td className="col-hide-md nowrap">{new Date(c.createdAt).toLocaleDateString(locale)}</td>
                {canManage && (
                  <td className="col-actions">
                    {c.status === "active" && (
                      <div className="row-actions">
                        <button type="button" className="button button-secondary button-sm" onClick={() => setModal({ card: c, status: "lost" })}>
                          <Icon name="alert" size={15} />
                          <span className="button-label">{t("card.markLost")}</span>
                        </button>
                        <button type="button" className="button button-secondary button-sm" onClick={() => setModal({ card: c, status: "revoked" })}>
                          <Icon name="ban" size={15} />
                          <span className="button-label">{t("card.revoke")}</span>
                        </button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal && <CardStatusDialog card={modal.card} status={modal.status} operatorName={name(modal.card.operatorId)} onClose={() => setModal(null)} />}
    </>
  );
}

function PermissionsTable({ data, permissions, showOperator, canManage }: { data: TenantData; permissions: OperatorPermission[]; showOperator?: boolean; canManage: boolean }) {
  const { t } = useI18n();
  const { provider } = useSession();
  const write = useOperatorWrite();
  const [removing, setRemoving] = useState<OperatorPermission | null>(null);
  const now = today();
  const name = (id: string) => data.operators.operators.find((o) => o.id === id)?.fullName ?? t("common.empty");
  return (
    <>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              {showOperator && <th scope="col">{t("perm.operator")}</th>}
              <th scope="col">{t("perm.scope")}</th>
              <th scope="col">{t("perm.validUntil")}</th>
              {canManage && (
                <th scope="col" className="col-actions">
                  <span className="sr-only">{t("common.actions")}</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {permissions.map((p) => (
              <tr key={p.id} className={permissionExpired(p, now) ? "row-muted" : undefined}>
                {showOperator && (
                  <td>
                    <a href={`#/operators/${encodeURIComponent(p.operatorId)}`} className="row-link">
                      {name(p.operatorId)}
                    </a>
                  </td>
                )}
                <td>{permissionScope(data, p, t)}</td>
                <td>
                  <DayCell day={p.validUntil} empty={t("perm.openEnded")} /> {permissionExpired(p, now) && <span className="badge tone-neutral">{t("perm.expired")}</span>}
                </td>
                {canManage && (
                  <td className="col-actions">
                    <button type="button" className="icon-button" onClick={() => setRemoving(p)} title={t("perm.remove")}>
                      <Icon name="trash" size={16} label={t("perm.remove")} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {removing && (
        <ConfirmDialog
          title={t("perm.removeTitle")}
          confirmLabel={t("perm.remove")}
          danger
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            await write(() => provider.deletePermission(removing.id));
            setRemoving(null);
          }}
        >
          <p>{t("perm.removeBody", { name: name(removing.operatorId), scope: permissionScope(data, removing, t) })}</p>
        </ConfirmDialog>
      )}
    </>
  );
}

/* ---------------- Operators list ---------------- */

export function OperatorsList({ data }: { data: TenantData }) {
  const { t } = useI18n();
  const { role } = useSession();
  const canManage = canManageOperators(role);
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<OperatorStatus | "">("");
  const now = today();
  const { operators, documents, cards } = data.operators;
  const rows = useMemo(
    () =>
      operators
        .map((o) => ({ o, status: operatorStatus(o, documents, now) }))
        .filter((r) => (status ? r.status === status : r.status !== "left"))
        .filter((r) => {
          const s = q.trim().toLocaleLowerCase("tr");
          return !s || r.o.fullName.toLocaleLowerCase("tr").includes(s) || (r.o.employeeNo ?? "").toLocaleLowerCase("tr").includes(s);
        })
        .sort((a, b) => a.o.fullName.localeCompare(b.o.fullName, "tr")),
    [operators, documents, now, q, status],
  );
  const nextExpiry = (id: string) =>
    documents
      .filter((d) => d.operatorId === id && d.required)
      .map((d) => d.validUntil)
      .sort()[0] ?? null;

  return (
    <>
      <PageHeader
        title={t("operators.title")}
        subtitle={t("operators.subtitle", { count: operators.filter((o) => operatorStatus(o, documents, now) !== "left").length })}
        actions={
          canManage && (
            <button type="button" className="button button-primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={16} />
              {t("operators.add")}
            </button>
          )
        }
      />
      <div className="filters" role="group" aria-label={t("operators.filtersLabel")}>
        <div className="search">
          <Icon name="search" size={16} className="search-icon" />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("operators.search")} aria-label={t("operators.search")} />
        </div>
        <div className="field field-inline">
          <label htmlFor="f-opstatus">{t("operators.filterStatus")}</label>
          <select id="f-opstatus" value={status} onChange={(e) => setStatus(e.target.value as OperatorStatus | "")}>
            <option value="">{t("operators.allCurrent")}</option>
            {OPERATOR_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`opStatus.${s}`)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <Panel flush>
        {rows.length === 0 ? (
          <EmptyState icon="user" title={operators.length === 0 ? t("operators.empty") : t("operators.noMatch")}>
            {operators.length === 0 && (canManage ? t("operators.emptyManager") : t("operators.emptyViewer"))}
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t("operator.fullName")}</th>
                  <th scope="col" className="col-hide-sm">
                    {t("operator.employeeNo")}
                  </th>
                  <th scope="col" className="col-hide-md">
                    {t("operator.site")}
                  </th>
                  <th scope="col">{t("operator.status")}</th>
                  {canManage && (
                    <>
                      <th scope="col" className="num col-hide-sm">
                        {t("operator.cards")}
                      </th>
                      <th scope="col" className="col-hide-md">
                        {t("operator.nextExpiry")}
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ o, status: st }) => (
                  <tr key={o.id}>
                    <td>
                      <a href={`#/operators/${encodeURIComponent(o.id)}`} className="row-link strong">
                        {o.fullName}
                      </a>
                    </td>
                    <td className="col-hide-sm mono">{o.employeeNo ?? t("common.empty")}</td>
                    <td className="col-hide-md">{siteLabel(data, o.siteId) ?? <span className="muted">{t("operator.siteAll")}</span>}</td>
                    <td>
                      <OperatorStatusBadge status={st} />
                    </td>
                    {canManage && (
                      <>
                        <td className="num col-hide-sm">{cards.filter((c) => c.operatorId === o.id && c.status === "active").length}</td>
                        <td className="col-hide-md">
                          <DayCell day={nextExpiry(o.id)} />
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {!canManage && <Note>{t("operators.readOnly")}</Note>}
      {creating && <OperatorFormDialog data={data} onClose={() => setCreating(false)} onSaved={(id) => navigate(`/operators/${encodeURIComponent(id)}`)} />}
    </>
  );
}

/* ---------------- Operator detail ---------------- */

type DetailModal =
  | { kind: "edit" }
  | { kind: "leave" }
  | { kind: "card" }
  | { kind: "doc"; doc?: OperatorDocument }
  | { kind: "deleteDoc"; doc: OperatorDocument }
  | { kind: "perm" }
  | null;

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="dl-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function OperatorDetail({ data, operatorId }: { data: TenantData; operatorId: string }) {
  const { t, locale } = useI18n();
  const { role, provider } = useSession();
  const write = useOperatorWrite();
  const canManage = canManageOperators(role);
  const [modal, setModal] = useState<DetailModal>(null);
  const o = data.operators.operators.find((x) => x.id === operatorId);
  const now = today();
  if (!o) {
    return (
      <Panel>
        <EmptyState icon="user" title={t("operator.notFound")} action={<a href="#/operators" className="button button-secondary">{t("operators.back")}</a>} />
      </Panel>
    );
  }
  const status = operatorStatus(o, data.operators.documents, now);
  const cards = data.operators.cards.filter((c) => c.operatorId === o.id);
  const docs = data.operators.documents.filter((d) => d.operatorId === o.id).sort((a, b) => a.validUntil.localeCompare(b.validUntil));
  const perms = data.operators.permissions.filter((p) => p.operatorId === o.id);
  const input = { ...data.operators, sites: data.sites };
  const vehicles = data.vehicles.filter((v) => v.active && authorizedOperators(v, input, now).some((a) => a.operator.id === o.id));
  const editable = canManage && !o.anonymized;

  return (
    <>
      <PageHeader
        eyebrow={t("nav.operators")}
        title={o.fullName}
        subtitle={<OperatorStatusBadge status={status} />}
        actions={
          editable && (
            <>
              <button type="button" className="button button-secondary" onClick={() => setModal({ kind: "edit" })}>
                <Icon name="edit" size={16} />
                {t("common.edit")}
              </button>
              {!o.leftOn && (
                <button type="button" className="button button-secondary" onClick={() => setModal({ kind: "leave" })}>
                  <Icon name="logout" size={16} />
                  {t("operator.leave")}
                </button>
              )}
            </>
          )
        }
      />
      {status === "suspended" && <Note>{t("operator.suspendedNote")}</Note>}
      {o.leftOn && !o.anonymized && <Note>{t("operator.leftNote", { day: formatDay(o.leftOn, locale) ?? o.leftOn })}</Note>}
      {o.anonymized && <Note>{t("operator.anonymizedNote")}</Note>}

      <div className="detail-grid">
        <Panel title={t("operator.identity")}>
          <dl className="dl">
            <Field label={t("operator.employeeNo")}>
              <span className="mono">{o.employeeNo ?? t("common.empty")}</span>
            </Field>
            <Field label={t("operator.site")}>{siteLabel(data, o.siteId) ?? t("operator.siteAll")}</Field>
            <Field label={t("operator.leftOn")}>
              <DayCell day={o.leftOn} />
            </Field>
            <Field label={t("operator.authorizedVehicles")}>
              {vehicles.length === 0 ? (
                <span className="muted">{t("operator.noVehicles")}</span>
              ) : (
                vehicles
                  .sort((a, b) => a.fleetNo.localeCompare(b.fleetNo, "tr", { numeric: true }))
                  .map((v, i) => (
                    <span key={v.id}>
                      {i > 0 && ", "}
                      <a href={`#/vehicles/${encodeURIComponent(v.id)}/operators`} className="subtle-link mono">
                        {v.fleetNo}
                      </a>
                    </span>
                  ))
              )}
            </Field>
          </dl>
        </Panel>

        {canManage && (
          <Panel
            title={t("operator.cards")}
            meta={
              editable && (
                <button type="button" className="button button-secondary button-sm" onClick={() => setModal({ kind: "card" })}>
                  <Icon name="plus" size={15} />
                  {t("card.add")}
                </button>
              )
            }
            flush
          >
            {cards.length === 0 ? <p className="table-empty">{t("card.none")}</p> : <CardsTable data={data} cards={cards} canManage={editable} />}
          </Panel>
        )}

        {canManage && (
          <Panel
            title={t("operator.documents")}
            meta={
              editable && (
                <button type="button" className="button button-secondary button-sm" onClick={() => setModal({ kind: "doc" })}>
                  <Icon name="plus" size={15} />
                  {t("doc.add")}
                </button>
              )
            }
            flush
          >
            {docs.length === 0 ? (
              <p className="table-empty">{t("doc.none")}</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">{t("doc.kind")}</th>
                      <th scope="col">{t("doc.validUntil")}</th>
                      <th scope="col" className="col-hide-sm">
                        {t("doc.required")}
                      </th>
                      {editable && (
                        <th scope="col" className="col-actions">
                          <span className="sr-only">{t("common.actions")}</span>
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map((d) => (
                      <tr key={d.id}>
                        <td>
                          {t(`docKind.${d.kind}`)}
                          {d.label && <span className="muted"> · {d.label}</span>}
                        </td>
                        <td>
                          <DayCell day={d.validUntil} /> <DocWarningBadge doc={d} now={now} />
                        </td>
                        <td className="col-hide-sm">{d.required ? t("common.yes") : t("common.no")}</td>
                        {editable && (
                          <td className="col-actions">
                            <div className="row-actions">
                              <button type="button" className="icon-button" onClick={() => setModal({ kind: "doc", doc: d })} title={t("common.edit")}>
                                <Icon name="edit" size={16} label={t("common.edit")} />
                              </button>
                              <button type="button" className="icon-button" onClick={() => setModal({ kind: "deleteDoc", doc: d })} title={t("doc.delete")}>
                                <Icon name="trash" size={16} label={t("doc.delete")} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        )}

        <Panel
          title={t("operator.permissions")}
          meta={
            editable && (
              <button type="button" className="button button-secondary button-sm" onClick={() => setModal({ kind: "perm" })}>
                <Icon name="plus" size={15} />
                {t("perm.add")}
              </button>
            )
          }
          flush
        >
          {perms.length === 0 ? <p className="table-empty">{t("perm.none")}</p> : <PermissionsTable data={data} permissions={perms} canManage={editable} />}
        </Panel>
      </div>

      {modal?.kind === "edit" && <OperatorFormDialog data={data} operator={o} onClose={() => setModal(null)} />}
      {modal?.kind === "leave" && <LeaveDialog operator={o} onClose={() => setModal(null)} />}
      {modal?.kind === "card" && <CardDialog operator={o} onClose={() => setModal(null)} />}
      {modal?.kind === "doc" && <DocumentDialog operator={o} doc={modal.doc} onClose={() => setModal(null)} />}
      {modal?.kind === "perm" && <PermissionDialog data={data} operator={o} onClose={() => setModal(null)} />}
      {modal?.kind === "deleteDoc" && (
        <ConfirmDialog
          title={t("doc.deleteTitle")}
          confirmLabel={t("doc.delete")}
          danger
          onClose={() => setModal(null)}
          onConfirm={async () => {
            await write(() => provider.deleteDocument(modal.doc.id));
            setModal(null);
          }}
        >
          <p>{t("doc.deleteBody", { kind: t(`docKind.${modal.doc.kind}`) })}</p>
        </ConfirmDialog>
      )}
    </>
  );
}

/* ---------------- Cards screen ---------------- */

export function CardsScreen({ data }: { data: TenantData }) {
  const { t } = useI18n();
  const { role } = useSession();
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);
  const needle = q.replace(/[\s:.-]/g, "").toUpperCase();
  const cards = data.operators.cards.filter((c) => (showAll || c.status === "active") && (!needle || c.uid.includes(needle)));
  return (
    <>
      <PageHeader title={t("cards.title")} subtitle={t("cards.subtitle", { count: data.operators.cards.filter((c) => c.status === "active").length })} />
      <div className="filters" role="group" aria-label={t("cards.filtersLabel")}>
        <div className="search">
          <Icon name="search" size={16} className="search-icon" />
          <input type="search" className="mono" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("cards.search")} aria-label={t("cards.search")} />
        </div>
        <label className="check-row">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          <span>{t("cards.showInactive")}</span>
        </label>
      </div>
      <Panel flush>
        {cards.length === 0 ? (
          <EmptyState icon="card" title={data.operators.cards.length === 0 ? t("cards.empty") : t("cards.noMatch")}>
            {data.operators.cards.length === 0 && t("cards.emptyHint")}
          </EmptyState>
        ) : (
          <CardsTable data={data} cards={cards} showOperator canManage={canManageOperators(role)} />
        )}
      </Panel>
      <Note>{t("cards.note")}</Note>
    </>
  );
}

/* ---------------- Permissions screen ---------------- */

export function PermissionsScreen({ data }: { data: TenantData }) {
  const { t } = useI18n();
  const { role } = useSession();
  const canManage = canManageOperators(role);
  const [adding, setAdding] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const now = today();
  const name = (id: string) => data.operators.operators.find((o) => o.id === id)?.fullName ?? "";
  const live = new Set(data.operators.operators.filter((o) => !o.anonymized).map((o) => o.id));
  const perms = data.operators.permissions
    .filter((p) => live.has(p.operatorId) && (showExpired || !permissionExpired(p, now)))
    .sort((a, b) => name(a.operatorId).localeCompare(name(b.operatorId), "tr"));
  return (
    <>
      <PageHeader
        title={t("permissions.title")}
        subtitle={t("permissions.subtitle")}
        actions={
          canManage && (
            <button type="button" className="button button-primary" onClick={() => setAdding(true)}>
              <Icon name="plus" size={16} />
              {t("perm.add")}
            </button>
          )
        }
      />
      <div className="filters">
        <label className="check-row">
          <input type="checkbox" checked={showExpired} onChange={(e) => setShowExpired(e.target.checked)} />
          <span>{t("permissions.showExpired")}</span>
        </label>
      </div>
      <Panel flush>
        {perms.length === 0 ? (
          <EmptyState icon="key" title={t("permissions.empty")}>
            {t("permissions.emptyHint")}
          </EmptyState>
        ) : (
          <PermissionsTable data={data} permissions={perms} showOperator canManage={canManage} />
        )}
      </Panel>
      <Note>{t("permissions.note")}</Note>
      {adding && <PermissionDialog data={data} onClose={() => setAdding(false)} />}
    </>
  );
}

/* ---------------- Vehicle detail: authorised operators ---------------- */

export function VehicleOperatorsPanel({ data, vehicle }: { data: TenantData; vehicle: Vehicle }) {
  const { t, locale } = useI18n();
  const { role } = useSession();
  const now = today();
  const list = authorizedOperators(vehicle, { ...data.operators, sites: data.sites }, now);
  const status = vehicle.deviceId ? data.authlist.find((a) => a.deviceId === vehicle.deviceId) : undefined;
  const noReader = !!vehicle.deviceId && data.devices.some((d) => d.deviceId === vehicle.deviceId && !d.hasCardReader);
  const showCards = canManageOperators(role);
  return (
    <Panel
      title={t("vehicleOps.title")}
      meta={vehicle.deviceId ? <AuthlistBadge status={status} noReader={noReader} /> : <span className="muted">{t("vehicle.noDevice")}</span>}
      flush
    >
      {list.length === 0 ? (
        <p className="table-empty">{t("vehicleOps.none")}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t("operator.fullName")}</th>
                <th scope="col">{t("vehicleOps.until")}</th>
                {showCards && (
                  <th scope="col" className="num col-hide-sm">
                    {t("operator.cards")}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {list.map((a) => (
                <tr key={a.operator.id}>
                  <td>
                    <a href={`#/operators/${encodeURIComponent(a.operator.id)}`} className="row-link">
                      {a.operator.fullName}
                    </a>
                  </td>
                  <td>{a.until ? formatDay(a.until, locale) : <span className="muted">{t("perm.openEnded")}</span>}</td>
                  {showCards && <td className="num col-hide-sm">{a.cards.length}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="panel-footnote">{t("vehicleOps.note")}</p>
    </Panel>
  );
}
